<template>
  <div class="relative" :class="colSpan">
    <div class="relative flex items-center">
      <!-- INPUT -->

      <Field
        :id="handle || field.handle"
        :value="value"
        :name="handle || field.handle"
        :label="label || field.label || field.display"
        :type="type || field.config.input_type || field.type"
        :as="tag"
        :placeholder="handle || field.handle"
        :disabled="disabled"
        :maxlength="field.config.character_limit || 9999"
        :rules="
          field.config.input_type === 'tel'
            ? `${rulesAsString}|phone`
            : rulesAsString
        "
        :class="[
          error
            ? `
              border-notification-red
              focus:border-transparent
            `
            : 'border-transparent',
          { 'min-h-32': (type || field.type) === 'textarea' },
        ]"
        class="
          peer block w-full rounded-sm border bg-gray-100 px-6 pb-[0.8rem]
          pt-[1.2rem] transition
          placeholder:text-transparent
        "
        rows="4"
        :autocomplete="autocomplete"
        @focus="hasFocus = true"
        @blur="hasFocus = false"
      />

      <!-- LABEL -->
      <!-- eslint-disable tailwindcss/classnames-order //it automatically sorts the classes in the wrong order and then complains -->
      <!-- eslint-disable readable-tailwind/sort-classes //it automatically sorts the classes in the wrong order and then complains -->
      <label
        :for="handle || field.handle"

        class="
          absolute inset-x-6 top-1.5 text-xs text-black/60 transition-all
          peer-placeholder-shown:top-4 peer-placeholder-shown:text-base
          peer-placeholder-shown:text-black
          peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-black/60
        "
      >
        <!-- eslint-enable tailwindcss/classnames-order -->
        <!-- eslint-enable readable-tailwind/sort-classes -->
        <Transition name="fade">
          <span
            v-if="hasFocus"
            class="
              absolute inset-x-0 truncate
              first-letter:uppercase
            "
          >
            {{ placeholderText }}{{ isRequired ? '*' : '' }}
          </span>
          <span
            v-else
            class="
              absolute inset-x-0 truncate
              first-letter:uppercase
            "
          >
            {{ label || field.label || field.display
            }}{{ isRequired ? '*' : '' }}
          </span>
        </Transition>
      </label>
    </div>

    <!-- ERROR -->
    <TransitionExpand
      class="transition-all duration-300 ease-in-out"
      :open="!!error"
    >
      <p
        v-if="error"
        class="hyphens-auto pt-1 text-sm"
        :class="containerBackground === 'white' ? 'text-red-500' : 'text-white'"
      >
        {{ error }}
      </p>
    </TransitionExpand>

    <!-- INSTRUCTIONS -->
    <p
      v-if="instructions || field.instructions"
      class="hyphens-auto pt-1 text-sm"
    >
      {{ instructions || field.instructions }}
    </p>
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

const props = defineProps({
  handle: {
    type: String, default: null,
  },
  field: {
    type: Object, default: null,
  },
  value: {
    type: null, default: '',
  },
  placeholder: {
    type: String, default: '',
  },
  rules: {
    type: [Array, String], default: '',
  },
  error: {
    type: String, default: null,
  },
  disabled: {
    type: Boolean, default: false,
  },
  label: {
    type: String, default: '',
  },
  type: {
    type: String, default: null,
  },
  width: {
    type: [Number, String], default: null,
  },
  autocomplete: {
    type: String, default: null,
  },
  instructions: {
    type: String, default: null,
  },
  containerBackground: {
    type: String,
    default: 'white',
  },
})

const hasFocus = ref(false)

const tag = computed(() => {
  if ((props.type || props.field?.type) === 'textarea') {
    return 'textarea'
  }

  return 'input'
})

const rulesAsString = useFilterVeeValidateRules(props.rules)

const isRequired = computed(() => {
  return props.rules.includes('required')
})

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'md:col-span-6 lg:col-span-3 col-span-12'
    case 33:
      return 'md:col-span-6 lg:col-span-4 col-span-12'
    case 50:
      return 'md:col-span-6 lg:col-span-6 col-span-12'
    case 66:
      return 'md:col-span-6 lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})

const placeholderText = computed(() => {
  return (
    props.placeholder
    || props.field?.config?.placeholder
    || props.label
    || props.field?.label
    || props.field?.display
  )
})
</script>

<style scoped>
input[type='date']::-webkit-inner-spin-button,
input[type='date']::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}
</style>
